import { OidcClient } from "oidc-client-ts";
import { companySlugFrom } from "./companyHint.js";
import { getUserManager } from "./userManager.js";
/** Shape of a Signup Code. Keycloak lowercases the code it reads, so only lowercase is accepted here. */
const SIGNUP_CODE = /^[a-z0-9][a-z0-9._-]{0,99}$/;
/**
 * The registrations params for a sign-up link, or undefined when there is no usable company.
 *
 * A sign-up link — `/signup/<company-slug>/<group-code>` on a poster or in a campaign email — is the friendly front
 * for Keycloak's native sign-up. `company` and `groupCode` must match the names `ThriveSignupAuthenticator` reads:
 * the company pre-selects the employer, and a group code alongside it lands the member straight on the details form.
 *
 * Malformed values are dropped rather than passed through, as for the sign-in hint: a mangled code degrades to the
 * method step (where the member can still type it), and a mangled company to the plain company step.
 */
export function signupLinkParams(company, groupCode) {
    const slug = companySlugFrom(company);
    if (!slug)
        return undefined;
    const code = groupCode?.trim().toLowerCase();
    return code && SIGNUP_CODE.test(code) ? { company: slug, groupCode: code } : { company: slug };
}
/**
 * Keycloak's registrations endpoint for an authorize request URL. It is the authorize endpoint's sibling and takes
 * exactly the same params, so the state, nonce and PKCE challenge carry over unchanged.
 */
export function registrationsUrl(authorizeUrl) {
    const url = new URL(authorizeUrl);
    if (!url.pathname.endsWith("/protocol/openid-connect/auth")) {
        throw new Error("Sign-up needs a Keycloak authority.");
    }
    url.pathname = url.pathname.replace(/\/auth$/, "/registrations");
    return url.toString();
}
/**
 * Send the browser to Keycloak's native sign-up instead of sign-in.
 *
 * oidc-client-ts only knows the authorize endpoint, so this builds the same request `signinRedirect()` would — through
 * the shared settings and state store, tagged as a redirect sign-in — and retargets it. The callback is then an
 * ordinary `signinRedirectCallback()`: Keycloak finishes registration by issuing a code to the same `redirect_uri`.
 *
 * [returnTo] rides the OIDC state exactly as it does for `signIn()`, so the callback's `readReturnTo` lands the new
 * member on it. Anything but an app-relative path is dropped here rather than carried to the IdP and back.
 */
export async function signUpRedirect(params, returnTo) {
    const um = getUserManager();
    const request = await new OidcClient(um.settings, um.metadataService).createSigninRequest({
        request_type: "si:r",
        ...(params ? { extraQueryParams: params } : {}),
        ...(returnTo && isAppPath(returnTo) ? { state: { returnTo } } : {}),
    });
    window.location.replace(registrationsUrl(request.url));
}
/** An app-relative path: starts with one `/`, so it cannot name another origin. */
export function isAppPath(path) {
    return path.startsWith("/") && !path.startsWith("//");
}
//# sourceMappingURL=signupLink.js.map